import {
  Box,
  Button,
  Flex,
  HStack,
  Stack,
  Text,
  Image,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import ImageWithBadge from '../../ImageWithBadge';
import DummyImage from '../../../../assets/dummy.png';
import StatusBadge from '../../StatusBadge';
import { Order } from '@/services/OrderManagement/types';
import React, { useState } from 'react';
import CustomizationDetailsModal from '../../CustomizationDetailsModal';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import CustomizedFieldsTooltip from '@/pages/AllOrders/GroupOrderDetailsSection/CustomizedFieldsTooltip';
import CustomizationIcon from '@/assets/svg/customizable-icon.svg';
import AddOnsTooltip from '@/pages/AllOrders/GroupOrderDetailsSection/AddonsTooltip';
import AddOnsIcon from '@/assets/svg/add-ons-icon.svg';
import { useGetMaintenanceImages } from '@/services/OrderManagement';
import ImageIcon from '@/assets/svg/image_upload.svg?react';
import MaintenanceImageModal from '@/components/Orders/MaintenanceImageModal';

interface IOrderCardProps {
  isDetailPage?: boolean;
  order?: Order;
  onCancelModalOpen?: () => void;
}

export const formatDateToLongFormat = (dateString: string): string => {
  const [month, day, year] = dateString.split(' ')[0].split('-');
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const dayOfMonth = date.getDate();
  const monthName = date.toLocaleString('en-US', { month: 'long' });
  const fullYear = date.getFullYear();
  return `${dayOfMonth} ${monthName} ${fullYear}`;
};

export const formatISOToShortDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const dayOfMonth = date.getDate();
  const monthName = date.toLocaleString('en-US', { month: 'short' });
  return `${dayOfMonth} ${monthName}`;
};

const OrderCard: React.FC<IOrderCardProps> = ({ order, onCancelModalOpen }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const orderId = order?.order_id;
  const { data: maintenanceImages = [], isLoading: imagesLoading } = useGetMaintenanceImages(
    orderId || '',
  );

  const hasImages = maintenanceImages.length > 0;

  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);

  const getDynamicButtons = (status: string) => {
    if (status === 'Cancelled') return null;
    if (status === 'Returned') return null;
    if (status === 'Order Confirmed') {
      return (
        <Button
          variant="outline"
          border="2px"
          py={2}
          px={3}
          width={{ base: '100%', lg: '166px' }}
          borderRadius="xl"
          fontSize="xs"
          fontFamily="primary"
          fontWeight="semibold"
          borderColor={'brand.primary.DEFAULT'}
          color={'brand.primary.DEFAULT'}
          _hover={{ bg: 'brand.primary.DEFAULT', color: 'text.white' }}
          onClick={onCancelModalOpen}
        >
          Cancel Order
        </Button>
      );
    }
    return null;
  };

  const transaction = order?.individual_transaction;
  const primaryImage =
    order?.image?.find((img: { is_primary: boolean; image_small: string }) => img.is_primary)
      ?.image_small ||
    order?.image?.[0]?.image_small ||
    DummyImage;

  return (
    <>
      <Box border="1px solid" borderColor="border.default" borderRadius="lg" overflow="hidden">
        <Flex direction={'column'}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justifyContent={'space-between'}
            py={3}
            px={4}
            bg={'background.tertiary'}
            fontFamily={'primary'}
            gap={2}
          >
            <Stack direction={'column'} gap={0}>
              <Text color={'text.secondary'} fontSize={'xs'}>
                Order Id
              </Text>
              <Text color={'text.primary'} fontSize={'xs'}>
                {order?.order_id}
              </Text>
            </Stack>
            <Stack direction={'row'} gap={4}>
              <Stack direction={'column'} gap={0}>
                <Text color={'text.secondary'} fontSize={'xs'}>
                  Order Placed
                </Text>
                <Text color={'text.primary'} fontSize={'xs'}>
                  {formatDateToLongFormat(order?.created_at ?? '')}
                </Text>
              </Stack>
              <Stack direction={'column'} gap={0}>
                <Text color={'text.secondary'} fontSize={'xs'}>
                  Total
                </Text>
                <Text color={'text.primary'} fontSize={'xs'}>
                  {transaction?.grand_total
                    ? `${getCurrencySymbol(order?.individual_transaction?.currency)}${Number(transaction.grand_total).toFixed(2)}`
                    : '-'}
                </Text>
              </Stack>
              <Stack direction={'column'} gap={0}>
                <Text color={'text.secondary'} fontSize={'xs'}>
                  Ship To
                </Text>
                <Text color={'text.primary'} fontSize={'xs'}>
                  {order?.shipping_address?.address_line_1}, {order?.shipping_address?.town_city}
                </Text>
              </Stack>
            </Stack>
          </Flex>

          <Flex p={4} gap={3} bg={'background.white'} direction={'column'} fontFamily={'primary'}>
            <Stack direction="row" gap={3} alignItems="center">
              {order?.cancelled_at ? (
                <Text color="status.error" fontSize={'sm'}>
                  Cancelled on {formatISOToShortDate(order.cancelled_at)}
                </Text>
              ) : order?.returned_at ? (
                <Text color="status.error" fontSize={'sm'}>
                  Returned on {formatISOToShortDate(order.returned_at)}
                </Text>
              ) : order?.delivered_at ? (
                <Text color="status.success" fontSize={'sm'}>
                  Delivered on {formatISOToShortDate(order.delivered_at)}
                </Text>
              ) : null}
            </Stack>

            <Flex
              direction={{ base: 'column', lg: 'row' }}
              justifyContent={'space-between'}
              alignItems={'start'}
              gap={4}
            >
              <HStack width={'full'} justify={'space-between'} spacing="4">
                <HStack>
                  <ImageWithBadge quantity={1} w={'80px'} h={'auto'} src={primaryImage} />
                  <Box>
                    <VStack alignItems={'start'}>
                      <Text>{order?.product}</Text>
                      <HStack fontSize="xs" color="text.secondary" spacing="1" align="center">
                        {order?.is_customized ? (
                          <CustomizedFieldsTooltip
                            customizedFields={order?.customized_fields}
                            category={order?.category_name || ''}
                          >
                            <Box
                              as="span"
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              w="20px"
                              h="20px"
                              cursor="pointer"
                              title="Customized"
                              bg="background.tertiary"
                              borderRadius="sm"
                            >
                              <Image
                                src={CustomizationIcon}
                                alt="Customized"
                                w="16px"
                                h="16px"
                                objectFit="contain"
                              />
                            </Box>
                          </CustomizedFieldsTooltip>
                        ) : (
                          <Text as="span" fontWeight="medium" color="text.primary">
                            Size: {order?.size?.value || '—'}
                          </Text>
                        )}

                        {order?.customized_fields?.ProductAddOn_ids &&
                          order.customized_fields.ProductAddOn_ids.length > 0 &&
                          Array.isArray(order.customized_fields.add_ons) && (
                            <AddOnsTooltip addOns={order.customized_fields.add_ons}>
                              <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                w="20px"
                                h="20px"
                                cursor="pointer"
                                title="Add-ons"
                                bg="background.tertiary"
                                borderRadius="sm"
                              >
                                <Image
                                  src={AddOnsIcon}
                                  alt="Add-ons"
                                  w="20px"
                                  h="20px"
                                  objectFit="contain"
                                />
                              </Box>
                            </AddOnsTooltip>
                          )}
                      </HStack>
                    </VStack>
                  </Box>
                </HStack>
                <Box>
                  <HStack>
                    {orderId && hasImages && (
                      <IconButton
                        aria-label="View maintenance images"
                        icon={<ImageIcon color="#FEA01A" />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => setIsImageModalOpen(true)}
                        isLoading={imagesLoading}
                      />
                    )}

                    <StatusBadge status={order?.order_status?.status_name ?? ''} />
                  </HStack>
                </Box>
              </HStack>

              <Flex
                direction={{ base: 'row', lg: 'column' }}
                justifyContent={{ base: 'space-between', lg: 'start' }}
                gap={'4'}
                width={{ base: '100%', lg: 'auto' }}
              >
                {getDynamicButtons(order?.order_status?.status_name ?? '')}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Box>
      <MaintenanceImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        images={maintenanceImages}
        title="ORDER COMPLETION IMAGE"
      />
      <CustomizationDetailsModal
        isOpen={isCustomizationModalOpen}
        onClose={() => setIsCustomizationModalOpen(false)}
        order={order}
      />
    </>
  );
};

export default OrderCard;
